import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  EntitySearchConfigsToolbar,
  EntitySearchRefetcher,
} from 'src/components/EntitySearch';
import { useEntitySearch } from 'src/components/EntitySearch';
import { InventoryFlattenedView } from 'src/components/Events/EventListing/InventoryEventListing/InventoryFlattenedView';
import { ListingSearchBox } from 'src/components/Listings/ListingFilterBar/ListingSearchBox';
import { SearchInfoBanner } from 'src/components/SearchInfoBanner';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import {
  ActiveSortTableColumnProvider,
  TableViewMode,
} from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  CatalogDataContextProvider,
  useCatalogDataContext,
} from 'src/contexts/CatalogDataContext';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import { ListingStatusContextProvider } from 'src/contexts/ListingStatusContext';
import {
  MultiSelectionContextProvider,
  MultiSelectScope,
  NO_GROUP_ID,
} from 'src/contexts/MultiSelectionContext';
import { StopwatchLoggingContextProvider } from 'src/contexts/StopwatchLoggingContext';
import { PosNoResultsFound } from 'src/core/POS/PosNoResultsFound';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { DividerLine } from 'src/core/ui';
import { useGetActiveAccountClientConfig } from 'src/hooks/useGetActiveAccountClientConfig';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { createInventorySearchPageUrl } from 'src/utils/advancedSearchUtils';
import { ListingTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { InventoryDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { DefaultListingTableSorting } from 'src/utils/constants/tableColumns';
import { removeDeepLinkUrlPartsFromRelativeUrl } from 'src/utils/deepLinkUtils';
import {
  DefaultListingQuery,
  EmptyListingQuery,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { EventPageLocationState } from 'src/utils/types/EventPageLocationState';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  Feature,
  InventorySearchResult,
  InventoryViewMode,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingQuery,
  UserSetting,
} from 'src/WebApiController';

import {
  getListingsForEvents,
  listingQueryValueTransformFromUrl,
  listingQueryValueTransformToUrl,
} from '../Inventory';
import { getDefaultReturnUrl } from '../InventoryEvent/constants';
import {
  ACTIVE_EVENT_IDS_2_QUERY_PARAM,
  ACTIVE_EVENT_IDS_QUERY_PARAM,
  TAB_EVENT_IDS_2_QUERY_PARAM,
  TAB_EVENT_IDS_QUERY_PARAM,
} from '../InventoryEvent/InventoryEvent.constants';
import { MainRoute } from '../MainRoute';
import * as styles from './InventorySearch.css';
import { InventorySearchFilterBar } from './InventorySearchFilterBar';
import { InventorySearchToolbar } from './InventorySearchToolbar';

export function InventorySearch() {
  const hasUnsoldTicketsFilterFeature = useUserHasFeature(
    Feature.UnsoldTicketsFilter
  );

  return (
    <FilterQueryContextProvider<ListingQuery>
      key={`inventory-search-${hasUnsoldTicketsFilterFeature}`}
      initialQuery={{
        ...DefaultListingQuery,
        viewMode: InventoryViewMode.FlattenedView,
        hasUnsoldTickets: hasUnsoldTicketsFilterFeature ? true : null,
      }}
      emptyQuery={EmptyListingQuery}
      queryValueTransformToUrl={listingQueryValueTransformToUrl}
      queryValueTransformFromUrl={listingQueryValueTransformFromUrl}
      saveQueryInUrl
    >
      <InventorySearchPage />
    </FilterQueryContextProvider>
  );
}

function InventorySearchPage() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const [activeSearchConfig, setActiveSearchConfig] =
    useState<InventorySearchResult>();

  const getCatalogDataExpandedCallback = useCallback(
    (ids: string[], f: ListingQuery) => {
      // Check to see if if is empty filter, query
      if (
        activeSearchConfig == null || // do not start query for items if there is no activeSearchConfig
        (f.searchConfigId && !f.entityIds?.length) // we don't want to query when the filter has the search config id but no entity ids
      ) {
        return Promise.resolve({});
      }

      // If we're querying by ids - we want to get the latest greatest
      // We're only using readonly-db if we're filtering
      const useReadOnlyDb = f.entityIds?.length ? false : true;

      return getListingsForEvents(
        ids,
        f,
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError('ListingClient.searchListingsForEvents', error, {
              ...f,
              eventIds: ids,
            });
          },
        },
        useReadOnlyDb,
        true /* doNotProcessListingGroups */,
        true /* includeInternalNotes */,
        true /* isForSearch */
      );
    },
    [activeAccountWebClientConfig, activeSearchConfig, trackError]
  );

  return (
    <StopwatchLoggingContextProvider>
      <CatalogDataContextProvider<ListingQuery>
        entityType={ActionOutboxEntityType.Listing}
        queryKey="getCatalogForListingOrders"
        getCatalogDataExpanded={getCatalogDataExpandedCallback}
        transformEventData={transformData}
        disableAutoRefresh
        ignoreMaxCount
        doNotLogPageLoad
      >
        <CatalogMultiSelectionContextProvider type={'listing'}>
          <MultiSelectionContextProvider
            isFlattenedView
            forceSelectionMode={{
              mode: MultiSelectScope.AllGroups,
              groupId: NO_GROUP_ID,
            }}
            initialAllGroupsIds={[NO_GROUP_ID]}
          >
            <InventorySearchContent
              activeSearchConfig={activeSearchConfig}
              setActiveSearchConfig={setActiveSearchConfig}
            />
          </MultiSelectionContextProvider>
        </CatalogMultiSelectionContextProvider>
      </CatalogDataContextProvider>
    </StopwatchLoggingContextProvider>
  );
}

function InventorySearchContent({
  activeSearchConfig,
  setActiveSearchConfig,
}: {
  activeSearchConfig: InventorySearchResult | undefined;
  setActiveSearchConfig: (a: InventorySearchResult | undefined) => void;
}) {
  const searchTitle = useContent(ContentId.SearchInventory);
  const hasSearchV2Feature = useUserHasFeature(Feature.AdvancedSearchV2);

  const { value: isEventGroupingEnabled } = useServerUserSetting<boolean>({
    id: UserSetting.GroupInventoryEventView,
  });

  const [localIsEventGroupingEnabled, setLocalIsEventGroupingEnabled] =
    useState(isEventGroupingEnabled ?? false);

  const location = useLocation();
  const navigate = useNavigate();
  const {
    errorInfo,
    isLoading: isCatalogDataLoading,
    isItemsLoading,
    eventsTransformed,
  } = useCatalogDataContext();

  const {
    selectedFilters,
    setSelectedFilters,
    onSetActiveSearchConfig,
    onSaveSearchResult,
    onDeleteSearchResult,
    onRequeryForTheResult,
    refetchData,
    disabled,
    setDisabled,
  } = useEntitySearch(
    activeSearchConfig,
    setActiveSearchConfig,
    InventoryViewMode.FlattenedView,
    (client, id) => client.getInventorySearchResult(id),
    (client, r) => client.saveInventorySearchResult(r)
  );

  // Only use value provided at the first time (should be from '/purchases')
  const returnUrl = useMemo(() => {
    const locationState = (location.state ?? {}) as EventPageLocationState;

    if (locationState.returnUrl && locationState.keepReturnUrlUnchanged) {
      return locationState.returnUrl;
    }

    const relativeUrl =
      locationState.returnUrl || getDefaultReturnUrl('purchases');

    return removeDeepLinkUrlPartsFromRelativeUrl(relativeUrl, [
      InventoryDeeplinkQueryParam,
      ACTIVE_EVENT_IDS_QUERY_PARAM,
      TAB_EVENT_IDS_QUERY_PARAM,
      ACTIVE_EVENT_IDS_2_QUERY_PARAM,
      TAB_EVENT_IDS_2_QUERY_PARAM,
    ]);
  }, [location.state]);

  const getActClientCfg = useGetActiveAccountClientConfig(
    'Inventory.ListingClient.getListingByListingId'
  );

  const getActivePosEntity = useCallback(
    async (
      posEntityId: number,
      curEntity?: ListingDetails | null,
      dataToGet?: string[] | null
    ) => {
      const activeClientCf = await getActClientCfg();

      const listingDetail = await new ListingClient(
        activeClientCf
      ).getListingByListingId(
        posEntityId,
        curEntity ?? undefined,
        dataToGet?.map((s) => s as ListingDetailDataField) ?? []
      );

      if (listingDetail) {
        return {
          posEntityId: listingDetail.id,
          posEntity: listingDetail,
          posEntityDisplayId: listingDetail.idOnMkp,
        };
      }

      return {};
    },
    [getActClientCfg]
  );

  const dataIsLoading = isCatalogDataLoading;

  const onRedirectToSearchV2 = useCallback(() => {
    const searchUrl = createInventorySearchPageUrl(hasSearchV2Feature);

    // Having small delay so the click is a bit more natural
    setTimeout(() => {
      navigate(searchUrl, {
        state: {
          returnUrl: returnUrl,
        },
      });
    }, 10);
  }, [hasSearchV2Feature, navigate, returnUrl]);

  return (
    <ActivePosEntityProvider<ListingDetails>
      entityType={ActionOutboxEntityType.Listing}
      getActivePosEntity={getActivePosEntity}
    >
      <ListingStatusContextProvider>
        <ActiveSortTableColumnProvider
          entityType={ActionOutboxEntityType.Listing}
          viewMode={TableViewMode.SearchView}
          defaultSorting={DefaultListingTableSorting}
        >
          <DialogProvider>
            <LayoutContent
              mainRoute={MainRoute.InventorySearch}
              routeTitle={searchTitle}
              returnUrl={returnUrl}
              rightContent={
                <EntitySearchRefetcher
                  activeSearchConfig={activeSearchConfig}
                  disabled={disabled}
                  onRequeryForTheResult={onRequeryForTheResult}
                />
              }
              centerContent={
                <>
                  {hasSearchV2Feature ? (
                    <SearchInfoBanner
                      isOnSearchV2={false}
                      onRedirectClick={onRedirectToSearchV2}
                    />
                  ) : null}
                </>
              }
            >
              <EntitySearchConfigsToolbar
                disabled={disabled}
                setDisabled={setDisabled}
                activeSearchConfig={activeSearchConfig}
                setActiveSearchConfig={onSetActiveSearchConfig}
                saveActiveSearchConfig={onSaveSearchResult}
                deleteActiveSearchConfig={onDeleteSearchResult}
                getSearchConfigs={(client, includeAllUser) =>
                  client.getInventorySearchConfigs(includeAllUser)
                }
                getSearchResult={(client, id) =>
                  client.getInventorySearchResult(id)
                }
                additionalActions={<ListingSearchBox />}
              />
              <DividerLine style={{ margin: 0 }} />
              <InventorySearchFilterBar
                activeSearchConfig={activeSearchConfig}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                onSubmitFilter={(filter) => {
                  if (!activeSearchConfig) {
                    // Setting an empty search config so it starts to filter
                    onSetActiveSearchConfig({
                      id: null,
                      name: null,
                      query: filter,
                      queryV2: null,
                      ownerUserId: null,
                      count: null,
                      entityIds: null,
                      lastFetchDate: null,
                    });
                  }
                  refetchData(filter);
                }}
              />
              <DividerLine
                style={{ margin: 0, borderColor: vars.color.borderLight }}
              />
              <InventorySearchToolbar
                disabled={disabled}
                activeSearchConfig={activeSearchConfig}
                setLocalIsEventGroupingEnabled={setLocalIsEventGroupingEnabled}
                localIsEventGroupingEnabled={localIsEventGroupingEnabled}
              />
              <DividerLine
                style={{ margin: 0, borderColor: vars.color.borderLight }}
              />
              <div className={styles.contentContainer}>
                {errorInfo && (
                  <SomethingWentWrong
                    header={errorInfo.errorHeader}
                    message={
                      errorInfo.errorMessage ?? (
                        <Content id={ContentId.FailToLoadListContent} />
                      )
                    }
                  />
                )}
                {activeSearchConfig == null ? (
                  <PosNoResultsFound
                    mainMessage={ContentId.SearchToSeeResults}
                  />
                ) : (dataIsLoading || isItemsLoading) &&
                  !eventsTransformed?.length ? (
                  <PosSpinner />
                ) : eventsTransformed?.length ? (
                  <InventoryFlattenedView
                    alwaysShowCheckbox
                    showSelectAllOnHeader
                    doNotShowToolbar
                    enableColumnFilters={true}
                    ignoreMaxCount
                    // Slightly hacky but reliable way to group by event info
                    // See ListingTableColumnsConfig.tsx [ListingTableColumnId.Checkbox] for more details
                    groupingStateReadonly={
                      localIsEventGroupingEnabled
                        ? [ListingTableColumnId.Checkbox]
                        : undefined
                    }
                  />
                ) : (
                  <PosNoResultsFound
                    detailMessage={ContentId.AddFilterToShowResult}
                  />
                )}
              </div>
            </LayoutContent>
          </DialogProvider>
        </ActiveSortTableColumnProvider>
      </ListingStatusContextProvider>
    </ActivePosEntityProvider>
  );
}
